import { ComponentRef, Injectable } from '@angular/core';
import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { ComponentPortal, ComponentType } from '@angular/cdk/portal';
import {
  delay,
  map,
  merge,
  NEVER,
  Observable,
  of,
  startWith,
  Subject,
  take,
  tap,
} from 'rxjs';
import { ToastContainerComponent } from '../components/util/toast-container/toast-container.component';

@Injectable({
  providedIn: 'root',
})
export class CustomToastService {
  private overlayRef!: OverlayRef;
  private toastContainerRef: ComponentRef<ToastContainerComponent> | null =
    null;

  public dismissEventList: {
    toastId: number;
    dismissEvent: Subject<any>;
  }[] = [];

  private readonly animationDuration = 200;

  constructor(private overlay: Overlay) {}

  private init(): void {
    this.overlayRef = this.overlay.create({
      hasBackdrop: false,
    });
    const containerPortal = new ComponentPortal(ToastContainerComponent);
    this.toastContainerRef = this.overlayRef.attach(containerPortal);
    this.overlayRef.updatePosition();
    this.overlayRef.hostElement.classList.add(
      'app-toast-cdk-overlay-container'
    );
    this.overlayRef.addPanelClass('app-toast-cdk-overlay-container');
  }

  showToast<TComponent, TRes>(
    params: Partial<CustomToastParams<TComponent>>
  ): Observable<TRes | null> {
    if (!this.overlayRef || !this.overlayRef.hasAttached()) {
      this.init();
    }
    const toastRef =
      this.toastContainerRef!.instance.toastContainer.createComponent(
        params.component!
      );
    Object.entries(params.componentInputs || {}).forEach(
      ([inputName, value]) => {
        toastRef.setInput(inputName, value);
      }
    );
    const toastId = Math.random();

    const dismissEvent = this.toastContainerRef!.instance.addToast<
      TComponent,
      TRes
    >(toastRef, toastId);

    this.dismissEventList.push({ toastId, dismissEvent });
    return (
      params.duration
        ? merge(dismissEvent, of(null).pipe(delay(params.duration))).pipe(
            take(1)
          )
        : dismissEvent.asObservable()
    ).pipe(
      tap((value) => {
        this.dismissToast(toastId);
      })
    );
  }

  triggerDismissEvent(toastId: number, value: any): void {
    this.dismissEventList
      .find((e) => e.toastId === toastId)!
      .dismissEvent.next(value || null);
    this.dismissEventList = this.dismissEventList.filter(
      (i) => i.toastId !== toastId
    );
  }

  private dismissToast(toastId: number) {
    if (!this.toastContainerRef) {
      return;
    }
    this.dismissEventList = this.dismissEventList.filter(
      (i) => i.toastId !== toastId
    );
    this.toastContainerRef.instance.dismissToast(toastId);
    const noToastsRemaining = this.dismissEventList.length < 1;
    if (noToastsRemaining) {
      setTimeout(() => {
        this.overlayRef.dispose();
      }, this.animationDuration);
    }
  }
}

export interface CustomToastParams<TComponent> {
  component: ComponentType<TComponent>;
  componentInputs: Record<string, any>;
  duration?: number; // ms
}
